import { useCallback, useRef, useState } from "react";

import { getApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import { showError, showSuccess } from "../../utils/toast.ts";

function downloadBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}

export function useSkillsExport() {
  const [exporting, setExporting] = useState(false);
  const inFlight = useRef(false);

  const exportExcel = useCallback(async (
    endpoint: string,
    params: URLSearchParams,
    fileName: string,
  ) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setExporting(true);
    try {
      const query = params.toString();
      const { data } = await getApi<Blob>(
        query ? `${endpoint}?${query}` : endpoint,
        accessToken(),
        "blob",
      );
      if (!(data instanceof Blob) || data.size === 0) {
        throw new Error("The export file is empty.");
      }
      if (data.type.includes("json")) {
        const response = JSON.parse(await data.text()) as { message?: string };
        throw new Error(response.message || "Unable to export the file.");
      }
      downloadBlob(data, fileName);
      showSuccess("Export downloaded successfully.");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to export the file.");
    } finally {
      inFlight.current = false;
      setExporting(false);
    }
  }, []);

  return { exporting, exportExcel };
}
